import React from 'react';
import ClassName from '../../interfaces/class-name.interface';
import Gap from '../../interfaces/gap.interface';
import TestId from '../../interfaces/test-id.interface';

export interface Props extends Gap, ClassName, TestId {
  /** The actual value */
  value: number;

  /** An optional label to describe the chart */
  label?: string;

  /** The maximum value, the `value` is compared to */
  max: number;

  /** Optionally render the chart in a smaller size (default: m) */
  size?: 's' | 'm';

  /** The visual variant to render (simple bar chart, gauge-like chart, ...) */
  variant?: 'bar' | 'gauge';
}

/** Renders a simple bar chart with a random animation delay */
const BarChart = ({ gap = 'none', max, size, value }: Props): React.ReactElement => (
  <div
    className={`
      bar-chart
      bg-gray-200
      overflow-hidden
      rounded-full
      ${size === 's' ? 'h-1' : 'h-2'}
      gap--${gap}
    `}
  >
    <div
      className={`
          bg-teal-500
          flex
          h-full
          items-center
          justify-center
          rounded-full
          text-teal-800
          grow
          randomized--${Math.floor(Math.random() * 10)}
          skeletonize
        `}
      style={{ width: `${(value / max) * 100}%` }}
      title={`${value}`}
    />
  </div>
);

/** Renders a custom gauge chart with a random animation delay */
const GaugeChart = ({ gap = 's', max, value }: Props): React.ReactElement => (
  <svg
    viewBox="0 0 120 101"
    xmlns="http://www.w3.org/2000/svg"
    className={`
      gauge-chart
      leading-none
      gap--${gap}
      skeletonize
    `}
    data-value={(value / max).toFixed(1)}
  >
    <g className={`gauge-chart__chart randomized--${Math.floor(Math.random() * 10)}`}>
      <path
        className="gauge-chart__section gauge-chart__section--01"
        d="M2.65057 77.6906C5.33793 86.4127 9.96032 94.2863 16.0165 100.81L19.681 97.4087C14.1256 91.4241 9.89016 84.2066 7.42891 76.2184L2.65057 77.6906Z"
      />
      <path
        className="gauge-chart__section gauge-chart__section--02"
        d="M1.49223 46.6427C0.515662 50.9382 0 55.4089 0 60C0 64.5911 0.515662 69.0618 1.49223 73.3573L6.36781 72.2489C5.47337 68.3146 5 64.2156 5 60C5 55.7844 5.47337 51.6854 6.36782 47.7511L1.49223 46.6427Z"
      />
      <path
        className="gauge-chart__section gauge-chart__section--03"
        d="M16.0165 19.1896C9.96032 25.7137 5.33793 33.5873 2.65058 42.3094L7.42891 43.7816C9.89016 35.7934 14.1256 28.5759 19.681 22.5913L16.0165 19.1896Z"
      />
      <path
        className="gauge-chart__section gauge-chart__section--04"
        d="M42.3094 2.65057C33.5874 5.33793 25.7137 9.96032 19.1896 16.0165L22.5913 19.681C28.5759 14.1256 35.7935 9.89016 43.7816 7.42891L42.3094 2.65057Z"
      />
      <path
        className="gauge-chart__section gauge-chart__section--05"
        d="M73.3573 1.49223C69.0618 0.515662 64.5911 0 60 0C55.4089 0 50.9382 0.515662 46.6427 1.49223L47.7511 6.36781C51.6854 5.47337 55.7844 5 60 5C64.2156 5 68.3146 5.47337 72.2489 6.36782L73.3573 1.49223Z"
      />
      <path
        className="gauge-chart__section gauge-chart__section--06"
        d="M100.81 16.0165C94.2863 9.96032 86.4127 5.33793 77.6907 2.65058L76.2184 7.42891C84.2066 9.89016 91.4241 14.1256 97.4087 19.681L100.81 16.0165Z"
      />
      <path
        className="gauge-chart__section gauge-chart__section--07"
        d="M117.349 42.3094C114.662 33.5874 110.04 25.7137 103.983 19.1896L100.319 22.5913C105.874 28.5759 110.11 35.7935 112.571 43.7816L117.349 42.3094Z"
      />
      <path
        className="gauge-chart__section gauge-chart__section--08"
        d="M118.508 73.3573C119.484 69.0618 120 64.5911 120 60C120 55.4089 119.484 50.9382 118.508 46.6427L113.632 47.7511C114.527 51.6854 115 55.7844 115 60C115 64.2156 114.527 68.3146 113.632 72.2489L118.508 73.3573Z"
      />
      <path
        className="gauge-chart__section gauge-chart__section--09"
        d="M103.983 100.81C110.04 94.2863 114.662 86.4127 117.349 77.6907L112.571 76.2184C110.11 84.2066 105.874 91.4241 100.319 97.4087L103.983 100.81Z"
      />
    </g>
    <g className="gauge-chart__holder">
      <circle className="gauge-chart__bg" cx="60" cy="60" r="40" />
      <text
        className="gauge-chart__value"
        x="50%"
        y="62%"
        textAnchor="middle"
        alignmentBaseline="middle"
        dominantBaseline="middle"
      >
        {value.toFixed(1)}
      </text>
    </g>
  </svg>
);

/** A small chart to display various numbers/statistics */
const MiniChart = ({
  className = '',
  gap = 'none',
  label,
  max,
  size = 'm',
  value,
  variant = 'bar',
  testId,
}: Props): React.ReactElement => (
  <figure
    className={`
      flex
      text-gray-700
      gap--${gap}
      ${variant === 'bar' ? 'flex-col-reverse' : 'flex-col items-center'}
      ${size === 's' ? 'text-sm' : ''}
      ${className}
    `}
    data-testid={testId}
  >
    {variant === 'bar' ? (
      <BarChart value={value} max={max} size={size} />
    ) : (
      <GaugeChart value={value} max={max} />
    )}
    {label ? (
      <figcaption className={variant === 'gauge' ? 'font-bold' : ''}>{label}</figcaption>
    ) : null}
  </figure>
);

export default MiniChart;
